/**
 * useHomeDashboardFilters — the shared filter/plot state machine for the home
 * dashboards (Pulse and OEM).
 *
 * Both dashboards drive the same `ActivityMap` and the same `PulseFilterPanel`/
 * card strip, so the filter logic is identical. This hook owns it once: the plot
 * mode, the per-layer client/server facets, the filter-panel section models +
 * their handlers, and the active count. Each dashboard keeps only its own `range`
 * (time selector) and renders the chrome.
 *
 * The GROUPS and LABELS sections use the same All / "(not grouped)" /
 * "(not labeled)" + items meta-pill model as the rest of the app's filter panels
 * (shared-ui MetaFilterPills), translated to the server filter exactly the way
 * ActivityFilterModel does — so the home map and the Activity feed never diverge
 * on what a selection means. ACTIVITY TYPES stays a simple "All" + multi-select.
 *
 * Pulse-specific bits (the People card's `personFilter`) live here too but are
 * simply unused by the OEM dashboard — it never calls `handleSelectPerson`, so
 * `personFilter` stays null and contributes 0 to the active count.
 *
 * Web-only — do NOT import from mobile code.
 */
import {
  DEFAULT_META_PILL_STATE,
  HomeEntityType,
  IssueType,
  MetaPillState,
  metaPillCount,
  metaPillToFilter,
  toggleMetaItem,
  toggleMetaPill,
  useAllWorkspaceGroups,
  useCurrentWorkspace,
  useWorkspaceLabels,
} from '@gripp/shared-logic';
import { tokens } from '@gripp/shared-ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type FilterSectionModel } from '../components/pulseFilterPanel';

/**
 * Activity-type pill ids span two server-side axes the activity query
 * OR-combines: severity buckets (urgent/pending/solved) and issue types
 * (routine/note). Selecting any subset shows activities matching ANY of them.
 */
const SEVERITY_BUCKET_IDS = ['urgent', 'pending', 'solved'];
const ACTIVITY_ISSUE_TYPES: Record<string, IssueType | undefined> = {
  routine: IssueType.Routine,
  note: IssueType.Note,
};

export interface HomeDashboardFilters {
  // ── Raw state ──
  plotMode: HomeEntityType | null;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  filterOpen: boolean;
  setFilterOpen: (v: boolean) => void;
  personFilter: { id: string; name: string } | null;
  setPersonFilter: (p: { id: string; name: string } | null) => void;
  activityTypes: string[];

  // ── Derived: ActivityMap inputs ──
  effectivePlotMode: HomeEntityType | null;
  effectiveStatusFilter: string | null;
  isActivityLayer: boolean;
  severityBuckets: string[];
  issueTypes: IssueType[];
  // Server-translated GROUPS / LABELS facets (meta-pill model → API fields).
  groupIds: string[] | undefined;
  includeUngrouped: boolean | undefined;
  labelIds: string[] | undefined;
  includeUnlabeled: boolean | undefined;
  /** Active LABELS selection count — feeds the activity-layer plot banner. */
  labelCount: number;

  // ── Derived: card strip inputs ──
  /** Single-valued status reflection for the card strip's stat-pill highlight. */
  cardStatusFilter: string | null;

  // ── Derived: filter panel inputs ──
  filterSections: FilterSectionModel[];
  activeFilterCount: number;

  // ── Handlers ──
  handleSelectPlot: (entityType: HomeEntityType) => void;
  handleSelectStatus: (entityType: HomeEntityType, status: string) => void;
  handleSelectPerson: (person: { id: string; name: string }) => void;
  clearPlot: () => void;
  clearFilters: () => void;
}

export function useHomeDashboardFilters(): HomeDashboardFilters {
  const { t } = useTranslation();

  // plotMode: null = resting ASSET view. Card-strip selection lifts this so a
  // card-select replots the map.
  const [plotMode, setPlotMode] = useState<HomeEntityType | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Sub-filter within the active plotting mode (e.g. activity status, routine
  // name), set by clicking a chart item.
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  // Person facet — set by clicking a person in the People card (Pulse only);
  // scopes the resting field map to the assets that person has scanned.
  // Independent of plotMode (People is non-plotting); re-clicking the active
  // person clears it.
  const [personFilter, setPersonFilter] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Filter panel (header filter button → half-screen panel) state.
  const [filterOpen, setFilterOpen] = useState(false);
  // Activity-type facet (multi): a subset of urgent/pending/solved (severity
  // buckets) and routine/note (issue types). When non-empty the map plots the
  // ACTIVITY layer, filtered server-side to the union (OR) of the selections.
  const [activityTypes, setActivityTypes] = useState<string[]>([]);
  // Asset-group + activity-label facets — the shared All / None / items meta-pill
  // model (see @gripp/shared-logic metaPillState), so the home panel, the filter
  // models and MetaFilterPills all behave identically.
  const [groupMeta, setGroupMeta] = useState<MetaPillState>(
    DEFAULT_META_PILL_STATE
  );
  const [labelMeta, setLabelMeta] = useState<MetaPillState>(
    DEFAULT_META_PILL_STATE
  );

  // Real options for the Groups / Labels sections. Both gate on the workspace id.
  const workspace = useCurrentWorkspace();
  const { groups } = useAllWorkspaceGroups(workspace?.id);
  const { labels } = useWorkspaceLabels(workspace?.id);

  // ── Effective map layer ──
  // The activity layer is "active" when an activity-TYPE facet (severity / type
  // pill) is set. LABELS are NOT here: matching gripp-os, a label narrows the
  // ASSET fleet rather than switching the map to activity points. A concrete
  // non-activity card selection (Asset/Routine/Product) takes precedence;
  // otherwise the activity-type facet decides activity-vs-resting.
  const activityLayerActive = activityTypes.length > 0;
  const effectivePlotMode: HomeEntityType | null =
    plotMode && plotMode !== HomeEntityType.Activity
      ? plotMode
      : activityLayerActive
      ? HomeEntityType.Activity
      : plotMode;
  const isActivityLayer = effectivePlotMode === HomeEntityType.Activity;

  // Activity-type pill ids split across the two server-side axes the activity
  // query OR-combines: severity buckets and issue types.
  const severityBuckets = activityTypes.filter((id) =>
    SEVERITY_BUCKET_IDS.includes(id)
  );
  const issueTypes = activityTypes
    .map((id) => ACTIVITY_ISSUE_TYPES[id])
    .filter((it): it is IssueType => !!it);

  // GROUPS / LABELS meta-pill state → server filter fields.
  const groupServer = metaPillToFilter(groupMeta);
  const labelServer = metaPillToFilter(labelMeta);
  const groupIds = groupServer.ids;
  const includeUngrouped = groupServer.includeNone;
  const labelIds = labelServer.ids;
  const includeUnlabeled = labelServer.includeNone;

  // statusFilter is the single client-side sub-filter for the NON-activity
  // layers. The activity layer filters server-side, so suppress it there.
  const effectiveStatusFilter = isActivityLayer ? null : statusFilter;

  // The Activity card's stat-pill highlight is single-valued; reflect it only
  // when exactly one severity bucket is selected (the panel owns the full multi
  // state). Other layers pass their real client-side sub-filter.
  const cardStatusFilter = isActivityLayer
    ? activityTypes.length === 1
      ? activityTypes[0]
      : null
    : effectiveStatusFilter;

  const toggle = (arr: string[], id: string): string[] =>
    arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  // ── Card-strip handlers ──
  // Toggle a card's plotting mode: selecting the active one again clears it.
  const handleSelectPlot = (entityType: HomeEntityType) => {
    setStatusFilter(null);
    if (entityType === HomeEntityType.Activity) {
      // Activity card toggles the activity layer (incl. its panel filters).
      if (isActivityLayer) {
        setPlotMode(null);
        setActivityTypes([]);
        setLabelMeta(DEFAULT_META_PILL_STATE);
      } else {
        setPlotMode(HomeEntityType.Activity);
      }
      return;
    }
    // Asset/Routine/Product layer — clear the activity-only facets and toggle.
    setActivityTypes([]);
    setLabelMeta(DEFAULT_META_PILL_STATE);
    setPlotMode((current) => (current === entityType ? null : entityType));
  };

  const clearPlot = () => {
    setPlotMode(null);
    setStatusFilter(null);
    setActivityTypes([]);
    setLabelMeta(DEFAULT_META_PILL_STATE);
  };

  // Click a chart item (stat pill / routine row). For the Activity card this
  // toggles the severity bucket in the multi activity-type facet (shared with
  // the panel); for the other layers it sets the single client-side sub-filter.
  const handleSelectStatus = (entityType: HomeEntityType, status: string) => {
    if (entityType === HomeEntityType.Activity) {
      // Drop a conflicting non-activity plot mode so the activity layer shows,
      // and clear its chart sub-filter. The activity layer ignores statusFilter,
      // so a stale value would silently filter the resting ASSET map once the
      // user later clears the activity types (back to resting).
      if (plotMode && plotMode !== HomeEntityType.Activity) setPlotMode(null);
      setStatusFilter(null);
      setActivityTypes((current) => toggle(current, status));
      return;
    }
    setPlotMode(entityType);
    setStatusFilter((current) =>
      plotMode === entityType && current === status ? null : status
    );
  };

  // Toggle the person facet from the People card: re-selecting the active
  // person clears it. Scopes the resting field map to that person's assets.
  const handleSelectPerson = (person: { id: string; name: string }) => {
    setPersonFilter((current) => (current?.id === person.id ? null : person));
  };

  // ── Filter-panel handlers ──
  // Activity types: a stale chart sub-filter and a conflicting card plot are
  // dropped so the activity layer shows (mirrors handleSelectStatus).
  const onToggleActivityType = (id: string) => {
    if (plotMode && plotMode !== HomeEntityType.Activity) setPlotMode(null);
    setStatusFilter(null);
    setActivityTypes((current) => toggle(current, id));
  };

  // Labels narrow the resting ASSET fleet (gripp-os model). When a label filter
  // becomes ACTIVE, drop any card plot so the map returns to the resting ASSET
  // view where the label filter takes effect, and clear the stale chart
  // sub-filter. Widening back to "All" (an inactive result) leaves the current
  // plot alone — so tapping "All" never silently clears a Routine/Product plot.
  const setLabelMetaWithSideEffects = (next: MetaPillState) => {
    setLabelMeta(next);
    if (metaPillCount(next) > 0) {
      if (plotMode) setPlotMode(null);
      setStatusFilter(null);
    }
  };

  // Three filter sections. ACTIVITY TYPES is a simple "All" + multi (coloured
  // dots); GROUPS / LABELS use the All / "(not grouped)" / "(not labeled)" +
  // items meta-pill model shared with the other panels. The handlers close over
  // `plotMode` for their side effects, so the memo depends on it.
  const filterSections = useMemo<FilterSectionModel[]>(() => {
    const allLabel = t('home.filters.all', 'All');
    return [
      {
        kind: 'multi',
        id: 'activityTypes',
        label: t('home.filters.activityTypes', 'Activity Types'),
        allLabel,
        options: [
          {
            id: 'urgent',
            label: t('home.activity.urgent', 'Urgent'),
            dotColor: tokens.colors.semantic.error,
          },
          {
            id: 'pending',
            label: t('home.activity.pending', 'Pending'),
            dotColor: tokens.colors.semantic.warning,
          },
          {
            id: 'solved',
            label: t('home.activity.solved', 'Solved'),
            dotColor: tokens.colors.semantic.success,
          },
          {
            id: 'routine',
            label: t('home.activity.routine', 'Routine'),
            dotColor: tokens.colors.semantic.info,
          },
          {
            id: 'note',
            label: t('home.activity.note', 'Note'),
            dotColor: tokens.colors.label.violet,
          },
        ],
        selected: activityTypes,
        onToggleOption: onToggleActivityType,
        onSelectAll: () => {
          if (plotMode && plotMode !== HomeEntityType.Activity)
            setPlotMode(null);
          setStatusFilter(null);
          setActivityTypes([]);
        },
      },
      {
        kind: 'meta',
        id: 'groups',
        label: t('home.filters.groups', 'Groups'),
        allLabel,
        noneLabel: t('home.filters.notGrouped', '(not grouped)'),
        includeAll: groupMeta.includeAll,
        includeNone: groupMeta.includeNone,
        items: groups.map((g) => ({ id: g.id, label: g.name })),
        selectedItems: groupMeta.items,
        onToggleAll: () => setGroupMeta((s) => toggleMetaPill(s, 'all')),
        onToggleNone: () => setGroupMeta((s) => toggleMetaPill(s, 'none')),
        onToggleItem: (id) => setGroupMeta((s) => toggleMetaItem(s, id)),
      },
      {
        kind: 'meta',
        id: 'labels',
        label: t('home.filters.labels', 'Labels'),
        allLabel,
        noneLabel: t('home.filters.notLabeled', '(not labeled)'),
        includeAll: labelMeta.includeAll,
        includeNone: labelMeta.includeNone,
        items: labels.map((l: { id: string; name: string }) => ({
          id: l.id,
          label: l.name,
        })),
        selectedItems: labelMeta.items,
        onToggleAll: () =>
          setLabelMetaWithSideEffects(toggleMetaPill(labelMeta, 'all')),
        onToggleNone: () =>
          setLabelMetaWithSideEffects(toggleMetaPill(labelMeta, 'none')),
        onToggleItem: (id) =>
          setLabelMetaWithSideEffects(toggleMetaItem(labelMeta, id)),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, activityTypes, groupMeta, labelMeta, groups, labels, plotMode]);

  // Header badge / panel title count = total active (non-default) selections
  // across the three sections plus the person facet (set by the People card).
  const activeFilterCount =
    activityTypes.length +
    metaPillCount(groupMeta) +
    metaPillCount(labelMeta) +
    (personFilter ? 1 : 0);

  // Reset clears every panel facet AND the person facet, returning the map to
  // the resting asset view.
  const clearFilters = () => {
    setPlotMode(null);
    setStatusFilter(null);
    setActivityTypes([]);
    setGroupMeta(DEFAULT_META_PILL_STATE);
    setLabelMeta(DEFAULT_META_PILL_STATE);
    setPersonFilter(null);
  };

  return {
    plotMode,
    searchQuery,
    setSearchQuery,
    filterOpen,
    setFilterOpen,
    personFilter,
    setPersonFilter,
    activityTypes,
    effectivePlotMode,
    effectiveStatusFilter,
    isActivityLayer,
    severityBuckets,
    issueTypes,
    groupIds,
    includeUngrouped,
    labelIds,
    includeUnlabeled,
    labelCount: metaPillCount(labelMeta),
    cardStatusFilter,
    filterSections,
    activeFilterCount,
    handleSelectPlot,
    handleSelectStatus,
    handleSelectPerson,
    clearPlot,
    clearFilters,
  };
}
